import { BaseService } from './BaseService';
import type { ProjectDto, CreateProjectRequest, UpdateProjectRequest } from '../types/project';
import type { PagedResult } from '../types/tenant';

/**
 * Project service for handling project operations.
 */
export class ProjectService extends BaseService {
  /**
   * Gets a paginated list of projects for a tenant.
   */
  async getProjects(
    tenantId: string,
    params?: {
      pageNumber?: number;
      pageSize?: number;
      searchTerm?: string;
      isActive?: boolean;
    }
  ): Promise<PagedResult<ProjectDto>> {
    const queryParams = new URLSearchParams();
    if (params?.pageNumber) queryParams.append('pageNumber', params.pageNumber.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());

    const url = `/projects/tenant/${tenantId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<PagedResult<ProjectDto>>(url);
  }

  /**
   * Gets a project by ID.
   */
  async getProject(id: string, tenantId?: string): Promise<ProjectDto> {
    const url = tenantId ? `/projects/${id}?tenantId=${tenantId}` : `/projects/${id}`;
    return this.get<ProjectDto>(url);
  }

  /**
   * Gets the current project from context.
   */
  async getCurrentProject(): Promise<ProjectDto> {
    return this.get<ProjectDto>('/projects/current');
  }

  /**
   * Gets all projects that the current user has access to.
   */
  async getUserProjects(tenantId?: string): Promise<ProjectDto[]> {
    const url = tenantId ? `/projects/mine?tenantId=${tenantId}` : `/projects/mine`;
    return this.get<ProjectDto[]>(url);
  }

  /**
   * Creates a new project.
   */
  async createProject(tenantId: string, data: CreateProjectRequest): Promise<ProjectDto> {
    return this.post<ProjectDto>(`/projects/tenant/${tenantId}`, data);
  }

  /**
   * Updates an existing project.
   */
  async updateProject(id: string, tenantId: string, data: UpdateProjectRequest): Promise<ProjectDto> {
    return this.put<ProjectDto>(`/projects/${id}?tenantId=${tenantId}`, data);
  }

  /**
   * Deletes a project.
   */
  async deleteProject(id: string, tenantId: string): Promise<void> {
    return this.delete<void>(`/projects/${id}?tenantId=${tenantId}`);
  }
}

export const projectService = new ProjectService();
